import { Controller } from "@hotwired/stimulus";
import { createControlChannelSubscription } from "../channels/control_channel";

/**
 * Layout-level controller for persistent WebSocket subscriptions.
 *
 * This controller lives on the <body> element and survives Turbo Frame
 * replacements. It handles real-time events that need to persist across
 * page state changes (form submission, processing overlay, etc).
 *
 * Events handled:
 * - set_next_question: Navigates to next question via Turbo (no full reload)
 * - hold_progress: Updates the "Please Wait" overlay progress bar
 * - progress: Triggers Turbo Frame reload when user advances
 * - alerts: Displays moderator alerts to respondents
 *
 * @class UserLayoutController
 * @extends Controller
 */
export default class extends Controller {
  static values = {
    surveyId: Number,
    userId: Number
  };

  /**
   * Called when controller connects to DOM (page load).
   * Creates ControlChannel subscription if survey context exists.
   */
  connect() {
    // Only subscribe if we have a survey context
    if (!this.surveyIdValue) return;

    try {
      this.controlSub = createControlChannelSubscription(
        this.surveyIdValue,
        this.userIdValue,
        {
          onAlert: (message, kind) => this.handleAlert(message, kind),
          onProgress: (payload) => this.handleProgress(payload),
          onSetNextQuestion: (position) => this.handleSetNextQuestion(position),
          onHoldProgress: (data) => this.updateHoldProgress(data)
        }
      );
    } catch (e) {
      console.warn('UserLayoutController: subscription failed', e);
    }

    // Listen for form submissions to execute pending redirects
    // This is on <body> so it survives Turbo Frame replacements
    this.boundHandleSubmitEnd = this.handleTurboSubmitEnd.bind(this);
    document.addEventListener('turbo:submit-end', this.boundHandleSubmitEnd);

    // Set up accessibility: inert management for overlays
    this.setupHoldOverlayObserver();

    // If hold overlay is already visible on page load, set main content inert
    const holdOverlay = document.querySelector('.hold-overlay');
    if (holdOverlay) {
      this.setMainContentInert(true);
    }
  }

  /**
   * Called when controller disconnects from DOM.
   * Cleans up WebSocket subscription and observers.
   */
  disconnect() {
    if (this.controlSub) {
      try {
        this.controlSub.unsubscribe();
      } catch (_) {
        // Ignore unsubscribe errors during teardown
      }
      this.controlSub = null;
    }

    // Clean up turbo:submit-end listener
    if (this.boundHandleSubmitEnd) {
      document.removeEventListener('turbo:submit-end', this.boundHandleSubmitEnd);
      this.boundHandleSubmitEnd = null;
    }

    // Clean up hold overlay observer
    if (this.holdOverlayObserver) {
      this.holdOverlayObserver.disconnect();
      this.holdOverlayObserver = null;
    }
  }

  /**
   * Handle moderator alert broadcasts.
   * Displays alert modal and handles navigation based on alert type.
   *
   * @param {string} message - Alert message to display
   * @param {string} kind - Alert type: alert_only, alert_and_go_to_question, alert_and_end_survey
   */
  handleAlert(message, kind) {
    if (window.showModeratorAlert) {
      window.showModeratorAlert(message, kind);
    } else {
      console.warn('showModeratorAlert not available, using fallback');
      alert(`Moderator Alert: ${message}`);
      if (kind === 'alert_and_go_to_question' || kind === 'alert_and_end_survey') {
        window.location.reload();
      }
    }
  }

  /**
   * Handle set_next_question broadcasts from moderator.
   * Navigates user to next question via Turbo without full page reload.
   *
   * If the user is currently submitting their answer, the redirect is deferred
   * until after submission completes. If the user has interacted with the form
   * (dirty form), they get a brief grace period to submit before being redirected.
   *
   * @param {number} position - The target question position
   */
  handleSetNextQuestion(position) {
    console.log('UserLayoutController: set_next_question received, position:', position);

    // If user is on a question form (answering a question), do NOT redirect immediately.
    // Let them finish answering - after submission, executePendingRedirect() will navigate.
    // This ensures users can always complete their current answer before being moved.
    if (this.hasActiveQuestionForm()) {
      console.log('UserLayoutController: Question form visible, deferring redirect until after submission');
      // Store the pending redirect - executePendingRedirect() in user_interface_controller will use this
      window.acupollPendingRedirect = position;
      // Show a subtle indicator that the survey has moved on
      this.showSurveyAdvancedIndicator();
      return;
    }

    // If user is on hold screen, end screen, processing screen, or other non-question page,
    // navigate them to where they should be
    console.log('UserLayoutController: No active question form, navigating');
    this.navigateToNextQuestion();
  }

  /**
   * Show a subtle indicator that the survey has advanced.
   * This is informational only - does NOT force navigation.
   * User can dismiss manually or it auto-clears on form submission.
   */
  showSurveyAdvancedIndicator() {
    // Remove any existing indicator
    const existing = document.getElementById('survey-advanced-indicator');
    if (existing) existing.remove();

    // Create a dismissible, low-priority notification with proper ARIA attributes
    const indicator = document.createElement('div');
    indicator.id = 'survey-advanced-indicator';
    indicator.className = 'alert alert-secondary survey-advanced-indicator';
    indicator.setAttribute('role', 'status');
    indicator.setAttribute('aria-live', 'polite');

    // Add message text
    indicator.textContent = 'Survey has advanced - submit your answer to continue';

    // Add dismiss button (using × character pattern matching codebase conventions)
    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'close';
    dismissBtn.setAttribute('aria-label', 'Close');
    dismissBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    dismissBtn.addEventListener('click', () => indicator.remove());
    indicator.appendChild(dismissBtn);

    document.body.appendChild(indicator);
  }

  /**
   * Check if there is an active question form on the page.
   * Returns true if user is viewing a question they can answer.
   * Returns false if user is on hold/waiting screen (already submitted answer).
   *
   * @returns {boolean} True if question form is visible and user can answer
   */
  hasActiveQuestionForm() {
    // If hold overlay is visible, user has already submitted - they're waiting
    // and should be navigated when survey advances
    const holdOverlay = document.querySelector('.hold-overlay');
    if (holdOverlay) {
      console.log('UserLayoutController.hasActiveQuestionForm: hold overlay visible, returning false');
      return false;
    }

    const form = document.getElementById('user-interface');
    console.log('UserLayoutController.hasActiveQuestionForm: form =', form);
    return form !== null;
  }

  /**
   * Navigate to the next question.
   */
  navigateToNextQuestion() {
    if (window.Turbo) {
      Turbo.visit('/user_interface/new?t=' + Date.now(), { action: 'replace' });
    } else {
      window.location.href = '/user_interface/new?t=' + Date.now();
    }
  }

  /**
   * Handle turbo:submit-end events to execute pending redirects.
   * Called after user submits their answer form - executes any redirect
   * that was deferred while they were answering.
   *
   * @param {CustomEvent} event - The turbo:submit-end event
   */
  handleTurboSubmitEnd(event) {
    // Only act if there's a pending redirect from a deferred set_next_question
    if (!window.acupollPendingRedirect) return;

    const targetPosition = window.acupollPendingRedirect;
    window.acupollPendingRedirect = null;

    console.log('UserLayoutController: Executing deferred redirect to position', targetPosition);

    // Remove the "survey advanced" indicator if showing
    const indicator = document.getElementById('survey-advanced-indicator');
    if (indicator) indicator.remove();

    // Small delay to ensure server has processed the answer
    setTimeout(() => {
      this.navigateToNextQuestion();
    }, 100);
  }

  /**
   * Handle progression broadcasts.
   * Informational only - does NOT trigger navigation.
   * Navigation is handled by handleSetNextQuestion() when moderator advances.
   *
   * @param {Object} payload - Progress event payload
   */
  handleProgress(payload) {
    // Progress events are informational - navigation happens via set_next_question
    // This prevents double-loads when moderator advances (both events fire)
    console.log('UserLayoutController: progress event received', payload);
  }

  /**
   * Update the hold overlay progress bar when receiving hold_progress events.
   * Called when other respondents complete the current question.
   *
   * @param {Object} data - The hold progress payload
   * @param {number} data.question_id - The question ID
   * @param {number} data.percentage - Percentage of respondents who have answered
   */
  updateHoldProgress(data) {
    const questionId = data.question_id;
    const percentage = data.percentage;

    // Guard clause - only update if hold overlay is visible
    const percentageBar = document.getElementById(`question-${questionId}-percentage-bar`);
    if (!percentageBar) return;

    // Update progress bar width and accessibility
    percentageBar.style.width = `${percentage}%`;
    percentageBar.setAttribute('aria-valuenow', percentage);

    // Update color based on 75% threshold (matches server-side logic)
    const isSuccess = percentage >= 75;
    percentageBar.classList.toggle('bg-primary', !isSuccess);
    percentageBar.classList.toggle('bg-success', isSuccess);

    // Update percentage text
    const percentageText = document.getElementById(`question-${questionId}-percentage-text`);
    if (percentageText) {
      percentageText.textContent = `${percentage}%`;
    }

    // Update marker style
    const percentageMarker = document.getElementById(`question-${questionId}-percentage-marker`);
    if (percentageMarker) {
      percentageMarker.classList.toggle('progress__marker--success', isSuccess);
    }
  }

  /**
   * Set up a MutationObserver to watch for hold overlay changes.
   * Toggles the inert attribute on main content based on overlay visibility.
   * This prevents keyboard interaction with forms behind the overlay.
   */
  setupHoldOverlayObserver() {
    const holdContainer = document.getElementById('hold-overlay-container');
    if (!holdContainer) return;

    this.holdOverlayObserver = new MutationObserver(() => {
      const hasOverlay = holdContainer.querySelector('.hold-overlay') !== null;
      this.setMainContentInert(hasOverlay);
    });

    this.holdOverlayObserver.observe(holdContainer, { childList: true, subtree: true });
  }

  /**
   * Set the main content area as inert to prevent keyboard interaction.
   * The HTML inert attribute prevents focus, click events, and screen reader access.
   *
   * @param {boolean} inert - True to make content inert, false to restore access
   */
  setMainContentInert(inert) {
    document.getElementById('main-content')?.toggleAttribute('inert', inert);
  }

  /**
   * Handle skip-to-content link click.
   * Focuses the first interactive element inside main content so that
   * subsequent Tab presses continue through the content, not back to top.
   *
   * @param {Event} event - Click event from skip link
   */
  skipToContent(event) {
    event.preventDefault();
    const main = document.getElementById('main-content');
    if (!main) return;

    // Find first focusable element inside main content
    const focusable = main.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusable) {
      focusable.focus();
      // Scroll into view if needed
      focusable.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback: focus main container (original behavior)
      main.focus();
    }
  }
}
